import React, { useEffect, useState } from "react";
import {
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Box,
  ButtonGroup,
  Button,
  useBreakpointValue,
  Text,
  Link,
  NumberInput,
  NumberInputStepper,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInputField,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker.css";
import { useUser } from "../../context/UserContext";
import "@js-joda/timezone";
import { LocalDate, ZoneId } from "@js-joda/core";
import { DateFilterCategory } from "../../utils/enum";
import {
  DatePeriodEnum,
  DateFilterType,
  dateFilterTypeFromJSON,
  dateFilterTypeToJSON,
  datePeriodEnumFromJSON,
  datePeriodEnumToJSON,
  MonthEnum,
  monthEnumFromJSON,
  monthEnumToJSON,
  QuarterEnum,
  quarterEnumFromJSON,
  quarterEnumToJSON,
  DateFilter,
} from "build-ts/relative_date";
import { getEnumNames } from "utils/nameUtils";

// Define props interface
interface DateFilterProps {
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  id?: string;
}

const DateFilterPicker: React.FC<DateFilterProps> = ({
  dateFilter,
  setDateFilter,
  dateFilterCategory,
  setDateFilterCategory,
}) => {
  const { defaultTimeZone } = useUser();
  const [dateError, setDateError] = useState<string | null>(null);
  const [isRefDatePickerOpen, setRefDatePickerOpen] = useState<boolean>(false);

  // Responsive layout adjustment
  const isMobile = useBreakpointValue({ base: true, md: false });

  /**
   * Handles the change between Fixed and Rolling date filter categories.
   * @param category - The selected DateFilterCategory (FIXED or ROLLING).
   */
  const handleFilterCategoryChange = (category: DateFilterCategory) => {
    setDateFilterCategory(category);
    // Reset date filter on type change
    setDateFilter({
      type: undefined,
      month: undefined,
      quarter: undefined,
      year: undefined,
      date_period: undefined,
      number: undefined,
      date_range: undefined,
    });
  };

  /**
   * Handles the change for Fixed Type selection.
   * @param e - The change event from the Fixed Type Select.
   */
  const handleFixedTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTypeStr = e.target.value;
    const selectedType = dateFilterTypeFromJSON(selectedTypeStr);
    setDateFilter({
      type: selectedType,
      month: undefined,
      quarter: undefined,
      year: undefined,
      date_period: undefined,
      number: undefined,
      date_range: undefined,
    });
  };

  /**
   * Handles the change for Rolling Type selection.
   * Parses combined type and period values (e.g., "PERIOD_TO_DATE_WEEK").
   * @param e - The change event from the Rolling Type Select.
   */
  const handleRollingTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value.startsWith("PERIOD_TO_DATE")) {
      // Example value: "PERIOD_TO_DATE_WEEK"
      const parts = value.split("_");
      if (parts.length < 4) {
        // Invalid format
        setDateFilter({
          ...dateFilter,
          type: undefined,
          date_period: undefined,
        });
        return;
      }
      const periodStr = parts[3];
      const period = datePeriodEnumFromJSON(periodStr);
      setDateFilter({
        ...dateFilter,
        type: DateFilterType.PERIOD_TO_DATE,
        date_period: period,
        number: undefined,
        date_range: undefined,
      });
    } else {
      // Other DateFilterTypes
      const type = dateFilterTypeFromJSON(value);
      setDateFilter({
        ...dateFilter,
        type: type,
        date_period: undefined,
        number: undefined,
        date_range: undefined,
      });
    }
  };

  /**
   * Handles the change for Year input.
   * @param e - The change event from the Year Input.
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const year = Number(e.target.value);
    setDateFilter({
      ...dateFilter,
      year: year,
    });
  };

  /**
   * Handles the change for Quarter selection.
   * @param e - The change event from the Quarter Select.
   */
  const handleQuarterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedQuarterStr = e.target.value;
    const selectedQuarter = quarterEnumFromJSON(selectedQuarterStr);
    setDateFilter({
      ...dateFilter,
      quarter: selectedQuarter,
    });
  };

  /**
   * Handles the change for Month selection.
   * @param e - The change event from the Month Select.
   */
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMonthStr = e.target.value;
    const selectedMonth = monthEnumFromJSON(selectedMonthStr);
    setDateFilter({
      ...dateFilter,
      month: selectedMonth,
    });
  };

  /**
   * Handles the change for Start Date selection using react-datepicker.
   * Converts the selected date to epoch seconds.
   * @param selectedDate - The selected Date object or null.
   */
  const handleStartDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      // If the date is cleared, reset the low_epoch_sec
      setDateFilter(prev => ({
        ...prev,
        date_range: {
          ...prev?.date_range,
          low_epoch_sec: undefined,
        },
      }));
      return;
    }

    // Extract the year, month, and day from the selectedDate
    // Note: `selectedDate.getMonth()` is zero-based, so we add 1.
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const day = selectedDate.getDate();
    const localDate = LocalDate.of(year, month, day);
    const zonedDateTime = localDate.atStartOfDay(ZoneId.of(defaultTimeZone));
    const epochSeconds = zonedDateTime.toEpochSecond();

    setDateFilter(prev => ({
      ...prev,
      date_range: {
        ...prev?.date_range,
        low_epoch_sec: epochSeconds,
        high_epoch_sec: prev?.date_range?.high_epoch_sec, // Preserve existing high_epoch_sec
      },
    }));
    setDateError(null);
  };

  /**
   * Handles the change for End Date selection using react-datepicker.
   * Converts the selected date to epoch seconds.
   * @param selectedDate - The selected Date object or null.
   */
  const handleEndDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      // If the date is cleared, reset the high_epoch_sec
      setDateFilter(prev => ({
        ...prev,
        date_range: {
          ...prev?.date_range,
          high_epoch_sec: undefined,
        },
      }));
      return;
    }

    // Extract the year, month, and day from the selectedDate
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1; // JS months are 0-based
    const day = selectedDate.getDate();
    const localDate = LocalDate.of(year, month, day);
    const zonedDateTime = localDate.atStartOfDay(ZoneId.of(defaultTimeZone));
    const epochSeconds = zonedDateTime.toEpochSecond();

    setDateFilter(prev => ({
      ...prev,
      date_range: {
        ...prev?.date_range,
        high_epoch_sec: epochSeconds,
        low_epoch_sec: prev?.date_range?.low_epoch_sec, // Preserve existing low_epoch_sec
      },
    }));
    setDateError(null);
  };

  /**
   * Handles the change for Reference Epoch Date.
   * @param selectedDate - The selected Date object or null.
   */
  const handleReferenceDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) return;

    // Get today's LocalDate in the default timezone
    const today = LocalDate.now(ZoneId.of(defaultTimeZone));
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1; // JS months are 0-based
    const day = selectedDate.getDate();
    const selectedLocalDate = LocalDate.of(year, month, day);

    // Check if the selected date is today
    if (selectedLocalDate.equals(today)) {
      setDateFilter(prev => ({
        ...prev,
        reference_date_epoc_sec: undefined,
      }));
      setRefDatePickerOpen(false);
      return;
    }
    const zonedDateTime = selectedLocalDate.atStartOfDay(ZoneId.of(defaultTimeZone));
    const epochSeconds = zonedDateTime.toEpochSecond();

    setDateFilter(prev => ({
      ...prev,
      reference_date_epoc_sec: epochSeconds,
    }));

    setRefDatePickerOpen(false);
    setDateError(null);
  };

  /**
   * Converts epoch seconds to a JavaScript Date object for react-datepicker.
   * @param epochSec - The epoch seconds to convert.
   * @returns A Date object or null.
   */
  const epochSecToJSDate = (epochSec?: number): Date | null => {
    if (!epochSec) return null;

    // Convert epoch seconds to a date in the user's timezone
    // Convert epoch seconds to milliseconds and create a Date
    const date = new Date(epochSec * 1000);

    // Get the date components in the user's timezone
    const userDate = new Date(date.toLocaleString("en-US", { timeZone: defaultTimeZone }));
    return new Date(userDate.getFullYear(), userDate.getMonth(), userDate.getDate());
  };

  /**
   * Handles the change for Exact Date selection.
   * @param selectedDate - The selected Date object or null.
   */
  const handleExactDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      setDateFilter(prev => ({
        ...prev,
        reference_date_epoc_sec: undefined,
      }));
      return;
    }

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1; // JS months are 0-based
    const day = selectedDate.getDate();
    const localDate = LocalDate.of(year, month, day);
    const zonedDateTime = localDate.atStartOfDay(ZoneId.of(defaultTimeZone));
    const epochSeconds = zonedDateTime.toEpochSecond();

    setDateFilter(prev => ({
      ...prev,
      reference_date_epoc_sec: epochSeconds,
    }));
  };

  /**
   * Validates the date range whenever either start or end date changes.
   * Ensures that the start date is not after the end date.
   */
  useEffect(() => {
    if (dateFilter?.date_range?.low_epoch_sec !== undefined && dateFilter?.date_range?.high_epoch_sec !== undefined) {
      if (dateFilter.date_range.low_epoch_sec > dateFilter.date_range.high_epoch_sec) {
        setDateError("Start date cannot be after end date.");
        setDateFilter(prev => ({
          ...prev,
          date_range: undefined,
        }));
      } else {
        setDateError(null);
      }
    }
  }, [dateFilter?.date_range]);

  return (
    <Box borderRadius="md" p={4} boxShadow="md">
      <VStack spacing={4} align="stretch">
        {/* Switcher Buttons */}
        <ButtonGroup isAttached variant="outline" size="sm">
          <Button
            onClick={() => handleFilterCategoryChange(DateFilterCategory.FIXED)}
            colorScheme={dateFilterCategory === DateFilterCategory.FIXED ? "purple" : "gray"}
            borderWidth={dateFilterCategory === DateFilterCategory.FIXED ? 2 : 1}
          >
            Fixed
          </Button>
          <Button
            onClick={() => handleFilterCategoryChange(DateFilterCategory.ROLLING)}
            colorScheme={dateFilterCategory === DateFilterCategory.ROLLING ? "purple" : "gray"}
            borderWidth={dateFilterCategory === DateFilterCategory.ROLLING ? 2 : 1}
          >
            Relative
          </Button>
        </ButtonGroup>

        {/* Fixed Date Filter Options */}
        {dateFilterCategory === DateFilterCategory.FIXED && (
          <>
            {/* Range Type Selection */}
            <FormControl id="range-type" isRequired>
              {/* <FormLabel fontWeight="bold">Fixed Type</FormLabel> */}
              <Select
                placeholder="Select fixed date"
                value={dateFilter?.type ? dateFilterTypeToJSON(dateFilter.type) : ""}
                onChange={handleFixedTypeChange}
                size="sm"
                borderRadius={6}
              >
                {/* Divider */}
                <option disabled>──────────</option>
                <option value="EXACT_DATE">On</option>
                {/* Divider */}
                <option disabled>──────────</option>
                <option value="EXACT_DATE_RANGE">Between</option>
                <option value="MONTH_YEAR">Month</option>
                <option value="QUARTER_YEAR">Quarter</option>
                <option value="YEAR_ONLY">Year</option>
              </Select>
            </FormControl>

            {/* Month-Year Selection */}
            {dateFilter?.type === DateFilterType.MONTH_YEAR && (
              <HStack spacing={4} flexDirection={isMobile ? "column" : "row"}>
                <FormControl id="month" isRequired>
                  <FormLabel fontWeight="bold">Month</FormLabel>
                  <Select
                    placeholder="Select month"
                    value={dateFilter?.month !== undefined ? monthEnumToJSON(dateFilter.month) : ""}
                    onChange={handleMonthChange}
                    size="sm"
                    borderRadius={6}
                  >
                    {getEnumNames(MonthEnum).map(m => (
                      <option key={m} value={m}>
                        {m.charAt(0) + m.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl id="year" isRequired>
                  <FormLabel fontWeight="bold">Year</FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter year"
                    value={dateFilter?.year || ""}
                    onChange={handleYearChange}
                    size="sm"
                  />
                </FormControl>
              </HStack>
            )}

            {/* Quarter-Year Selection */}
            {dateFilter?.type === DateFilterType.QUARTER_YEAR && (
              <HStack spacing={4} flexDirection={isMobile ? "column" : "row"}>
                <FormControl id="quarter" isRequired>
                  <FormLabel fontWeight="bold">Quarter</FormLabel>
                  <Select
                    placeholder="Select quarter"
                    value={dateFilter?.quarter !== undefined ? quarterEnumToJSON(dateFilter.quarter) : ""}
                    onChange={handleQuarterChange}
                    size="sm"
                    borderRadius={6}
                  >
                    {getEnumNames(QuarterEnum).map(q => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl id="year" isRequired>
                  <FormLabel fontWeight="bold">Year</FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter year"
                    value={dateFilter?.year || ""}
                    onChange={handleYearChange}
                    size="sm"
                    borderRadius={6}
                  />
                </FormControl>
              </HStack>
            )}

            {/* Year-Only Selection */}
            {dateFilter?.type === DateFilterType.YEAR_ONLY && (
              <FormControl id="year" isRequired>
                <FormLabel fontWeight="bold">Year</FormLabel>
                <Input
                  type="number"
                  placeholder="Enter year"
                  value={dateFilter.year || ""}
                  onChange={handleYearChange}
                  size="sm"
                />
              </FormControl>
            )}

            {/* Exact Date Range Selection with react-datepicker */}
            {dateFilter?.type === DateFilterType.EXACT_DATE_RANGE && (
              <>
                <HStack spacing={4} flexDirection={isMobile ? "column" : "row"}>
                  <FormControl id="start-date" isRequired>
                    <FormLabel fontWeight="bold">Start Date (≥)</FormLabel>
                    <DatePicker
                      selected={epochSecToJSDate(dateFilter?.date_range?.low_epoch_sec)}
                      onChange={(date: Date | null) => handleStartDateChange(date)}
                      customInput={<Input />}
                      dateFormat="dd-MMM-yyyy"
                      popperPlacement="bottom-start"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                  </FormControl>
                  <FormControl id="end-date" isRequired>
                    <FormLabel fontWeight="bold">End Date (≤)</FormLabel>
                    <DatePicker
                      selected={epochSecToJSDate(dateFilter?.date_range?.high_epoch_sec)}
                      onChange={(date: Date | null) => handleEndDateChange(date)}
                      customInput={<Input />}
                      dateFormat="dd-MMM-yyyy"
                      popperPlacement="bottom-start"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                  </FormControl>
                </HStack>
                {dateError && (
                  <Box color="red.500" fontSize="sm">
                    {dateError}
                  </Box>
                )}
              </>
            )}

            {/* Exact date Selection */}
            {dateFilter?.type === DateFilterType.EXACT_DATE && (
              <FormControl id="exact-date" isRequired>
                <FormLabel fontWeight="bold">Date</FormLabel>
                <DatePicker
                  selected={epochSecToJSDate(dateFilter?.reference_date_epoc_sec)}
                  onChange={handleExactDateChange}
                  customInput={<Input />}
                  dateFormat="dd-MMM-yyyy"
                  popperPlacement="bottom-start"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                />
              </FormControl>
            )}
          </>
        )}

        {/* Rolling Date Filter Options */}
        {dateFilterCategory === DateFilterCategory.ROLLING && (
          <>
            {/* Rolling Type Selection */}
            <FormControl id="rolling-type" isRequired>
              {/* <FormLabel fontWeight="bold">Rolling Type</FormLabel> */}
              <Select
                placeholder="Select relative Date"
                value={
                  dateFilter?.type === DateFilterType.PERIOD_TO_DATE && dateFilter.date_period !== undefined
                    ? `PERIOD_TO_DATE_${datePeriodEnumToJSON(dateFilter.date_period)}`
                    : dateFilterTypeToJSON(dateFilter?.type!)
                }
                onChange={handleRollingTypeChange}
                size="sm"
                borderRadius={6}
              >
                {/* Divider */}
                <option disabled>──────────</option>

                {/* Last and Next N Periods */}
                <option value="LAST_N_PERIOD">Last Period</option>
                <option value="THIS_PERIOD">This Period</option>
                <option value="NEXT_N_PERIOD">Next Period</option>

                {/* Divider */}
                <option disabled>──────────</option>

                {/* Period-to-Date Options */}
                <option value="PERIOD_TO_DATE_WEEK">Week to Date</option>
                <option value="PERIOD_TO_DATE_MONTH">Month to Date</option>
                <option value="PERIOD_TO_DATE_QUARTER">Quarter to Date</option>
                <option value="PERIOD_TO_DATE_YEAR">Year to Date</option>
              </Select>
            </FormControl>

            {/* Additional Fields for Rolling */}
            {(dateFilter?.type === DateFilterType.LAST_N_PERIOD ||
              dateFilter?.type === DateFilterType.NEXT_N_PERIOD) && (
              <HStack spacing={4} flexDirection={isMobile ? "column" : "row"}>
                <FormControl id="last_n" isRequired>
                  <FormLabel fontWeight="600"># of Time Periods</FormLabel>
                  {/* <Input
                    type="number"
                    placeholder="Number of time periods"
                    value={dateFilter?.number || ""}
                    onChange={(e) =>
                      setDateFilter({
                        ...dateFilter,
                        number: Number(e.target.value),
                      })
                    }
                    size="sm"
                  /> */}
                  <NumberInput
                    value={dateFilter?.number || ""}
                    onChange={(valueString, valueNumber) =>
                      setDateFilter({
                        ...dateFilter,
                        number: valueNumber || 0,
                      })
                    }
                    min={1}
                    size="sm"
                    borderRadius="md"
                    colorScheme="purple"
                  >
                    <NumberInputField placeholder="# Select" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl id="n_period" isRequired>
                  <FormLabel fontWeight="600">Define Time Period</FormLabel>
                  <Select
                    placeholder="Day, Month, etc."
                    value={dateFilter?.date_period !== undefined ? datePeriodEnumToJSON(dateFilter.date_period) : ""}
                    onChange={e =>
                      setDateFilter({
                        ...dateFilter,
                        date_period: datePeriodEnumFromJSON(e.target.value),
                      })
                    }
                    size="sm"
                    borderRadius={6}
                  >
                    {getEnumNames(DatePeriodEnum).map(p => (
                      <option key={p} value={p}>
                        {p.charAt(0) + p.slice(1).toLowerCase() + "s"}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
            )}

            {/* This Period Selection */}
            {dateFilter?.type === DateFilterType.THIS_PERIOD && (
              <HStack spacing={4} flexDirection={isMobile ? "column" : "row"}>
                <FormControl id="n_period" isRequired>
                  <FormLabel fontWeight="bold">Define Time Period</FormLabel>
                  <Select
                    placeholder="Day, month, etc."
                    value={dateFilter?.date_period !== undefined ? datePeriodEnumToJSON(dateFilter.date_period) : ""}
                    onChange={e =>
                      setDateFilter({
                        ...dateFilter,
                        date_period: datePeriodEnumFromJSON(e.target.value),
                      })
                    }
                    size="sm"
                    borderRadius={6}
                  >
                    {getEnumNames(DatePeriodEnum).map(p => (
                      <option key={p} value={p}>
                        {p.charAt(0) + p.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
            )}
            {/* Reference Epoch Section */}
            <HStack spacing={2} mt={1} fontSize="sm" color="gray.900" mb={0} align="center">
              <Link
                color="black.500"
                textDecoration="underline"
                fontWeight={600}
                onClick={() => setRefDatePickerOpen(!isRefDatePickerOpen)}
              >
                <Text mb={0} color="blue.500">
                  Select as of date
                </Text>
              </Link>
              {dateFilter?.reference_date_epoc_sec && (
                <Text mb={0} color="gray.700" fontWeight="medium">
                  (
                  {epochSecToJSDate(dateFilter.reference_date_epoc_sec)?.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  )
                </Text>
              )}
              <DatePicker
                selected={epochSecToJSDate(dateFilter?.reference_date_epoc_sec)}
                onChange={handleReferenceDateChange}
                open={isRefDatePickerOpen}
                onClickOutside={() => setRefDatePickerOpen(false)}
                dateFormat="dd-MMM-yyyy"
                showPopperArrow={false}
                shouldCloseOnSelect
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                //
                // Provide a hidden custom input so the default text field doesn't appear.
                // This ensures only the calendar pop-up is shown when the link is clicked.
                //
                customInput={<div style={{ display: "none" }} />}
              />
            </HStack>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default DateFilterPicker;
